import React, { useEffect, useState } from "react";
import { MainLayout } from "components/layout/MainLayout";
import { Card, CardHeader, CardBody, Button, Modal } from "components/ui";
import { useToast } from "hooks/useToast";
import { usePermission } from "hooks/usePermissions";
import { PageId, ActionCode } from "types/index";
import { IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/index";
import { AddOutlined, EditOutlined, DeleteOutline } from "@mui/icons-material";
import { lookupService } from "services/lookup.service";

type LookupListItem = {
  id: string;
  parentId: number | null;
  code: string;
  value: string;
  parentCode: string;
};

export const LookupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const canAdd = usePermission(PageId.LOOKUPS, ActionCode.ADD);
  const canEdit = usePermission(PageId.LOOKUPS, ActionCode.EDIT);
  const canDelete = usePermission(PageId.LOOKUPS, ActionCode.DELETE);

  const [page, setPage] = useState(1);
  const pageSize = 100;
  const languageId = 1;
  const [rows, setRows] = useState<LookupListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedRows = rows;
  const columnCount = 5;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await lookupService.getPageList(page, pageSize, languageId);
        const normalized = (res.data || []).map((u: any) => ({
          id: u.id,
          code: u.code,
          value: u.value,
          parentId: u.parentId,
          parentCode: u.parentCode,
        })) as LookupListItem[];
        setRows(normalized);
        setTotal(res.pagination.total);
      } catch (e: any) {
        const friendly =
          "An error occurred on the server. Please contact the administrator.";
        setError(e?.message || friendly);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [page]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const onAdd = () => {
    navigate(ROUTES.LOOKUP_NEW);
  };

  const onView = (id: string) => {
    navigate(`${ROUTES.LOOKUPS}/${id}/view`);
  };

  const onRequestDelete = (id: string) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const onConfirmDelete = async () => {
    if (!deletingId) return;
    try {
      await lookupService.remove(deletingId);
      const res = await lookupService.getPageList(page, pageSize, languageId);
      setRows(res.data);
      setTotal(res.pagination.total);
      toast.success("Lookup deleted successfully");
    } catch (e: any) {
      toast.error(e?.message || "Failed to delete");
    } finally {
      setConfirmOpen(false);
      setDeletingId(null);
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader title="Lookups" />
        <CardBody>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="text-sm text-secondary-600">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {canAdd && (
                <Button variant="primary" size="sm" onClick={onAdd} className="w-full sm:w-auto">
                  <AddOutlined fontSize="small" />
                  <span>Add</span>
                </Button>
              )}
            </div>
          </div>

          {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
          {isLoading && (
            <div className="mb-3 text-sm text-secondary-600">Loading...</div>
          )}

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {!isLoading && !error && pagedRows.length === 0 && (
              <div className="p-6 text-center text-secondary-600">No data</div>
            )}
            {pagedRows.map((u) => (
              <div
                key={u.id}
                className="bg-white border border-secondary-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-secondary-900 text-base">
                      {u.code}
                    </h3>
                    {u.parentCode && (
                      <p className="text-sm text-secondary-600 mt-1">
                        Parent: {u.parentCode}
                      </p>
                    )}
                  </div>
                  {u.parentId != null && (
                    <div className="flex items-center gap-1 ml-2">
                      {canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            navigate(`${ROUTES.LOOKUPS}/${u.id}/edit`)
                          }
                          className="p-2"
                        >
                          <EditOutlined fontSize="small" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onRequestDelete(u.id)}
                          className="p-2"
                        >
                          <DeleteOutline fontSize="small" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[100px]">
                      Value:
                    </span>
                    <span className="text-secondary-900 break-words">{u.value}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border border-secondary-200 rounded-md overflow-hidden">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Code
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Parent Code
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Value
                  </th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && !error && pagedRows.length === 0 && (
                  <tr>
                    <td
                      className="p-6 text-center text-secondary-600"
                      colSpan={columnCount}
                    >
                      No data
                    </td>
                  </tr>
                )}
                {pagedRows.map((u) => (
                  <tr
                    key={u.id}
                    className="border-t border-secondary-100 hover:bg-secondary-50"
                  >
                    <td className="p-3">{u.code}</td>
                    <td className="p-3">{u.parentCode}</td>
                    <td className="p-3">{u.value}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        {canEdit && u.parentId != null && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`${ROUTES.LOOKUPS}/${u.id}/edit`)
                            }
                            aria-label="Edit"
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        )}
                        {canDelete && u.parentId != null && (
                          <IconButton
                            size="small"
                            onClick={() => onRequestDelete(u.id)}
                            aria-label="Delete"
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={page === 1}
              className="w-full sm:w-auto"
            >
              Prev
            </Button>
            <div className="text-sm text-secondary-600 text-center">
              {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of{" "}
              {total}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={page === totalPages}
              className="w-full sm:w-auto"
            >
              Next
            </Button>
          </div>
        </CardBody>
      </Card>
      <Modal
        open={confirmOpen}
        title="Delete Lookup"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={onConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setDeletingId(null);
        }}
      >
        Are you sure you want to delete this lookup?
      </Modal>
    </MainLayout>
  );
};

export default LookupsPage;
